import { Component, DestroyRef, inject, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { MatTooltipModule } from '@angular/material/tooltip';
import { DataTableComponent } from '../../../../components/ui/data-table/data-table.component';
import { PaginationComponent } from '../../../../components/inputs/pagination/pagination.component';
import { SearchInputComponent } from '../../../../components/inputs/search-input/search-input.component';
import {
  StatusFilterComponent,
  StatusFilterOption,
} from '../../../../components/inputs/status-filter/status-filter.component';
import {
  SortDropdownComponent,
  SortDropdownGroup,
  buildSortGroups,
} from '../../../../components/inputs/sort-dropdown/sort-dropdown.component';
import { EmptyStateComponent } from '../../../../components/ui/empty-state/empty-state.component';
import { StatusComponent } from '../../../../components/ui/status/status.component';
import { PromptCardComponent } from '../../../../components/toasts-and-cards/prompt-card/prompt-card.component';
import { ToastService } from '../../../../services/toast/toast.service';
import { UssdApplicationsService } from '../../../../services/ussd/ussd-applications.service';
import { UserProfileService } from '../../../../services/user/user-profile.service';
import { UssdApplication, UssdApplicationStats } from '../../../../types/ussd.models';
import { formatWhen } from '../../../../../utils/sms';
import {
  appStatusView,
  countryName,
  describeHttpError,
  environmentLabel,
  plural,
  StatusView,
  USSD_ENVIRONMENTS,
} from '../ussd-display';

type StatusFilter = 'enabled' | 'disabled' | null;

/** Smallest size app-pagination offers. */
const MIN_PAGE_SIZE = 10;

interface AppRow {
  app: UssdApplication;
  name: string;
  detail: string;
  code: string;
  environment: string;
  country: string;
  created: string;
  status: StatusView;
}

@Component({
  selector: 'app-ussd-applications',
  standalone: true,
  imports: [
    FormsModule,
    MatTooltipModule,
    DataTableComponent,
    PaginationComponent,
    SearchInputComponent,
    StatusFilterComponent,
    SortDropdownComponent,
    EmptyStateComponent,
    StatusComponent,
    PromptCardComponent,
  ],
  templateUrl: './applications.component.html',
  styleUrl: './applications.component.css',
})
export class UssdApplicationsComponent implements OnInit {
  private router = inject(Router);
  private destroyRef = inject(DestroyRef);
  private applicationsService = inject(UssdApplicationsService);
  private userService = inject(UserProfileService);
  private toast = inject(ToastService);

  applications: UssdApplication[] = [];
  rows: AppRow[] = [];
  isLoading = false;
  errorMessage = '';

  currentPage = 0;
  pageSize = 20;
  totalElements = 0;
  totalPages = 0;

  stats: UssdApplicationStats | null = null;
  statsLoaded = false;

  searchTerm = '';
  statusFilter: StatusFilter = null;
  environmentFilter: string | null = null;
  /** Empty until the tester picks one, so the server's own order applies by default. */
  sort = '';

  selectedApp: UssdApplication | null = null;
  showDeleteConfirm = false;
  isDeleting = false;

  readonly plural = plural;

  readonly statusOptions: StatusFilterOption[] = [
    { value: null, title: 'All', icon: 'list', tone: 'neutral' },
    { value: 'enabled', title: 'Enabled', icon: 'check_circle', tone: 'success' },
    { value: 'disabled', title: 'Disabled', icon: 'block', tone: 'neutral' },
  ];

  readonly environmentOptions: StatusFilterOption[] = [
    { value: null, title: 'All', icon: 'layers', tone: 'neutral' },
    ...USSD_ENVIRONMENTS.map((e) => ({ value: e.key, title: e.label, icon: 'layers', tone: 'neutral' as const })),
  ];

  readonly sortGroups: SortDropdownGroup[] = buildSortGroups({
    nameField: 'name',
    nameLabel: 'Name',
    createdField: 'createdAt',
    modifiedField: 'modifiedAt',
  });

  get organisationId(): string {
    return this.userService.organizationId ?? '';
  }

  get isFiltered(): boolean {
    return !!this.searchTerm.trim() || !!this.statusFilter || !!this.environmentFilter;
  }

  /** "12 applications, 9 enabled, 3 disabled, 1 awaiting approval". */
  get summaryParts(): { value: number; label: string; tone?: 'wait' }[] {
    if (this.stats) {
      const s = this.stats;
      const parts: { value: number; label: string; tone?: 'wait' }[] = [
        { value: s.totalApplications ?? 0, label: plural(s.totalApplications ?? 0, 'application', 'applications') },
        { value: s.enabledApplications ?? 0, label: 'enabled' },
        { value: s.disabledApplications ?? 0, label: 'disabled' },
      ];
      if (s.pendingPromotions) {
        parts.push({ value: s.pendingPromotions, label: 'awaiting approval', tone: 'wait' });
      }
      return parts;
    }
    return [{ value: this.totalElements, label: plural(this.totalElements, 'application', 'applications') }];
  }

  get emptyMessage(): string {
    return 'No USSD applications yet';
  }

  /**
   * Keep the pager (and its size select) while there is more than the
   * smallest page size, so a larger size can be changed back.
   */
  get showPagination(): boolean {
    return this.totalPages > 1 || this.totalElements > MIN_PAGE_SIZE;
  }

  get emptyHint(): string {
    if (this.isFiltered) {
      return this.totalPages > 1 ? 'Search and filters cover the page shown. Try another page or clear them.' : '';
    }
    return 'Create one to get a service code you can dial in the simulator.';
  }

  ngOnInit(): void {
    this.fetchApplications();
    this.fetchStats();
  }

  fetchApplications(): void {
    if (!this.organisationId) return;

    this.isLoading = true;
    this.errorMessage = '';

    this.applicationsService
      .getApplications(this.organisationId, { page: this.currentPage, size: this.pageSize, sort: this.sort || undefined })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (data) => {
          this.applications = data.content ?? [];
          this.totalElements = data.totalElements;
          this.totalPages = data.totalPages;
          this.isLoading = false;
          this.buildRows();
        },
        error: (err) => {
          this.errorMessage = describeHttpError(err, 'Applications could not be loaded.');
          this.applications = [];
          this.buildRows();
          this.isLoading = false;
        },
      });
  }

  private fetchStats(): void {
    if (!this.organisationId) return;
    this.applicationsService
      .getStats(this.organisationId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (stats) => {
          this.stats = stats && typeof stats.totalApplications === 'number' ? stats : null;
          this.statsLoaded = true;
        },
        // The summary falls back to the page total.
        error: () => {
          this.stats = null;
          this.statsLoaded = true;
        },
      });
  }

  private buildRows(): void {
    const term = this.searchTerm.trim().toLowerCase();
    this.rows = this.applications
      .filter((app) => {
        if (this.statusFilter === 'enabled' && app.enabled === false) return false;
        if (this.statusFilter === 'disabled' && app.enabled !== false) return false;
        if (this.environmentFilter && (app.environment || 'DEVELOPMENT').toUpperCase() !== this.environmentFilter) {
          return false;
        }
        if (!term) return true;
        return [app.name, app.alias, app.serviceCode, app.description]
          .filter(Boolean)
          .some((value) => String(value).toLowerCase().includes(term));
      })
      .map((app) => ({
        app,
        name: app.name || 'Untitled application',
        detail: app.alias || app.description || '',
        code: app.serviceCode || '—',
        environment: environmentLabel(app.environment),
        country: app.countryCode || app.countryName ? countryName(app.countryCode, app.countryName) : '—',
        created: app.createdAt ? formatWhen(app.createdAt) : '—',
        status: appStatusView(app),
      }));
  }

  // ── Toolbar ──

  updateSearch(term: string): void {
    this.searchTerm = term ?? '';
    this.buildRows();
  }

  updateStatusFilter(value: string | null): void {
    this.statusFilter = (value as StatusFilter) ?? null;
    this.buildRows();
  }

  updateEnvironmentFilter(value: string | null): void {
    this.environmentFilter = value;
    this.buildRows();
  }

  updateSort(value: string): void {
    this.sort = value;
    this.currentPage = 0;
    this.fetchApplications();
  }

  clearFilters(): void {
    this.searchTerm = '';
    this.statusFilter = null;
    this.environmentFilter = null;
    this.buildRows();
  }

  // ── Pagination ──
  // app-pagination emits pageSizeChange and then changePage(0); only the
  // page change fetches, so a new page size loads once.

  onPageChange(page: number): void {
    this.currentPage = page;
    this.fetchApplications();
  }

  onPageSizeChange(size: number): void {
    this.pageSize = size;
    this.currentPage = 0;
  }

  // ── Navigation ──

  addApplication(): void {
    this.router.navigate(['/organization/ussd-simulator/applications/new']);
  }

  openSimulator(): void {
    this.router.navigate(['/organization/ussd-simulator/simulator']);
  }

  navigateToDetail(id: string): void {
    this.router.navigate(['/organization/ussd-simulator/applications', id]);
  }

  openFromKeyboard(event: Event, id: string): void {
    if (event.target !== event.currentTarget) return;
    event.preventDefault();
    this.navigateToDetail(id);
  }

  navigateToEdit(id: string): void {
    this.router.navigate(['/organization/ussd-simulator/applications', id, 'edit']);
  }

  testInSimulator(app: UssdApplication): void {
    this.router.navigate(['/organization/ussd-simulator/simulator'], {
      queryParams: { code: app.serviceCode || null, country: app.countryCode || null },
    });
  }

  // ── Delete ──

  confirmDelete(app: UssdApplication): void {
    this.selectedApp = app;
    this.showDeleteConfirm = true;
  }

  cancelDelete(): void {
    this.showDeleteConfirm = false;
    this.selectedApp = null;
  }

  deleteApplication(): void {
    if (!this.selectedApp || this.isDeleting) return;

    const app = this.selectedApp;
    this.isDeleting = true;
    this.applicationsService.deleteApplication(this.organisationId, app.id).subscribe({
      next: () => {
        this.toast.success(`${app.name} deleted.`);
        this.showDeleteConfirm = false;
        this.selectedApp = null;
        this.isDeleting = false;
        if (this.applications.length === 1 && this.currentPage > 0) this.currentPage -= 1;
        this.fetchApplications();
        this.fetchStats();
      },
      error: (err) => {
        this.toast.error(describeHttpError(err, `${app.name} was not deleted.`));
        this.showDeleteConfirm = false;
        this.selectedApp = null;
        this.isDeleting = false;
      },
    });
  }
}
